import React, {FC, useEffect, useMemo} from 'react';
import {useSelector} from 'react-redux';

import AemButton from '@/components/Aem/AemButton';
import useConfigExternalLink from '@/hooks/externalLink/useConfigExternalLink';
import {useJourneyInfo} from '@/hooks/loyalty/dataHooks';
import appContent from '@/services/content/appContent';
import {getAchievedBadgeImage} from '@/services/loyalty/badgeUtils';
import {getJourneyLevelProgress} from '@/services/loyalty/loyaltyUtils';
import {formatTime} from '@/services/timeFormatting';
import {selectLanguageCode} from '@/state/selectors/global';
import {LoyaltyPopupTemplate} from '@/views/Loyalty/PopupTemplates/primitives';

interface IExpiringBadgePopupTemplateProps {
    journeyId?: string;
    timeInSeconds?: number;
    onClose?: () => void;
    onAction?: () => void;
}

export const ExpiringBadgePopupTemplate: FC<IExpiringBadgePopupTemplateProps> = ({
    onAction,
    onClose,
    timeInSeconds,
    journeyId,
}) => {
    const langCode = useSelector(selectLanguageCode);
    const {currentLevel, currentLevelBadge, progressCompleted, progressTotal, journeyName, journeyDescription} =
        useJourneyInfo(journeyId) || {};

    const {lastNotCompletedAction} = useMemo(() => getJourneyLevelProgress(currentLevel), [currentLevel]);
    const {actionLabel, actionDescription} = lastNotCompletedAction || {};
    const {progressTitle, labelTimeConfig, button} = appContent.getExpiringBadgeContent();
    const {externalLink, alt} = button || {};
    const {redirectToExternalLink: redirect} = useConfigExternalLink({...externalLink, ssoParams: {journeyId}});

    useEffect(() => {
        if (!currentLevel) {
            onClose?.();
        }
    }, [currentLevel]);

    if (!currentLevel) {
        return null;
    }

    const onActionClick = () => {
        onAction?.();
        // @ts-ignore
        redirect?.();
    };

    return (
        <LoyaltyPopupTemplate>
            <LoyaltyPopupTemplate.Header handleClose={onClose} className='justify-between'>
                {timeInSeconds && (
                    <LoyaltyPopupTemplate.Chip>
                        {formatTime(timeInSeconds, labelTimeConfig, langCode)}
                    </LoyaltyPopupTemplate.Chip>
                )}
            </LoyaltyPopupTemplate.Header>
            <LoyaltyPopupTemplate.Body>
                <LoyaltyPopupTemplate.Hero imageSrc={getAchievedBadgeImage(currentLevelBadge)?.imageURL}>
                    <LoyaltyPopupTemplate.Title>{journeyName}</LoyaltyPopupTemplate.Title>
                    <LoyaltyPopupTemplate.Description>{journeyDescription}</LoyaltyPopupTemplate.Description>
                </LoyaltyPopupTemplate.Hero>
                <LoyaltyPopupTemplate.Footer className='bg-charcoal'>
                    <div className='flex w-full flex-col gap-4'>
                        <div className='flex w-full justify-between'>
                            <div className='pr-5 text-16 font-normal'>
                                <p className='text-tertiary'>{actionLabel}</p>
                                <p className='text-white'>{actionDescription}</p>
                            </div>
                        </div>
                        <LoyaltyPopupTemplate.Progress
                            title={progressTitle}
                            currentStep={progressCompleted!}
                            totalSteps={progressTotal}
                        />
                    </div>
                </LoyaltyPopupTemplate.Footer>
                <AemButton
                    aria-label={alt}
                    primary
                    dark
                    className='m-0 w-full px-6 py-3'
                    onClick={onActionClick}
                    {...button}
                />
            </LoyaltyPopupTemplate.Body>
        </LoyaltyPopupTemplate>
    );
};
